import { useCallback, useEffect } from "react";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import type { BookedTeleconsultoriaRow } from "../../provider/types";
import type { TeleconsultoriaDateRange } from "../../shared/hooks/useTeleconsultoriaDateRange";
import { listAgendaAppointments, listAgendaSlots } from "../services/agendaDetailsService";
import type { AgendaDetailsTab, AgendaSlotRow, TeleconsultoriaAgendaSummary } from "../types";

interface AgendaDetailsData {
  slots: AgendaSlotRow[];
  appointments: BookedTeleconsultoriaRow[];
}

const EMPTY_DETAILS: AgendaDetailsData = { slots: [], appointments: [] };

/**
 * Conteúdo da aba aberta no painel da agenda, no período do filtro.
 * Só busca a aba visível (como o AppointmentAccordion).
 */
export function useAgendaDetails(params: {
  agenda: TeleconsultoriaAgendaSummary | null;
  tab: AgendaDetailsTab;
  range: TeleconsultoriaDateRange;
}) {
  const { agenda, tab } = params;
  const { startDate, endDate } = params.range;

  const loadTask = useAsyncTask({
    task: useCallback(async (): Promise<AgendaDetailsData> => {
      if (!agenda) return EMPTY_DETAILS;
      const range = { startDate, endDate };
      if (tab === "slots") return { slots: await listAgendaSlots(agenda, range), appointments: [] };
      return {
        slots: [],
        appointments: await listAgendaAppointments({
          summary: agenda,
          range,
          kind: tab === "upcoming" ? "upcoming" : "done",
        }),
      };
    }, [agenda, tab, startDate, endDate]),
    initialData: EMPTY_DETAILS,
  });

  const { run } = loadTask;
  const reload = useCallback(() => {
    void run().catch((error) => console.error("useAgendaDetails", error));
  }, [run]);

  useEffect(() => {
    if (agenda) reload();
  }, [agenda, reload]);

  const data = loadTask.data ?? EMPTY_DETAILS;
  return {
    slots: data.slots,
    appointments: data.appointments,
    isLoading: loadTask.loading,
    hasError: loadTask.status === "error",
    reload,
  };
}
